/**
 * Whether an inline CTA sits outside the resting About-Elite FAB chip.
 *
 * Belongs here: show FAB only after the buy CTA’s position in the scroll content is idle
 * (layout finished — Rive/images may take 200ms–1s+), then apply viewport overlap.
 * Does not belong here: sticky CTA lift, FAB animation, page layout.
 */
import { useEffect, useState, type RefObject } from 'react'
import { getEliteAboutFabViewportSlot } from './eliteAboutFabLayout'

const MAX_ATTACH_FRAMES = 60
/** CTA top/height in scroll content unchanged this long → treat hero layout as settled. */
const CTA_LAYOUT_IDLE_MS = 180

/**
 * Document position of the CTA inside the scroll root (scroll-invariant).
 * Layout growth above the CTA changes this; user scroll does not.
 */
function readCtaLayoutKey(inlineCta: HTMLElement, scrollRoot: HTMLElement): string | null {
  try {
    const ctaRect = inlineCta.getBoundingClientRect()
    const rootRect = scrollRoot.getBoundingClientRect()
    if (!ctaRect || !rootRect || (ctaRect.height ?? 0) <= 0) return null
    const yInContent = (scrollRoot.scrollTop ?? 0) + (ctaRect.top - rootRect.top)
    return `${Math.round(yInContent)}:${Math.round(ctaRect.height)}`
  } catch {
    return null
  }
}

function isInlineCtaClearOfViewportFab(inlineCta: HTMLElement): boolean {
  try {
    const ctaRect = inlineCta.getBoundingClientRect()
    if (!ctaRect || (ctaRect.height ?? 0) <= 0) return false

    const visualViewport = window.visualViewport
    const viewportTop = visualViewport?.offsetTop ?? 0
    const viewportHeight = visualViewport?.height ?? window.innerHeight ?? 0
    const viewportBottom = viewportTop + viewportHeight
    if (!(viewportBottom > 0)) return false

    const { fabTopFromViewportBottom, fabBottomFromViewportBottom } = getEliteAboutFabViewportSlot()
    const fabTop = viewportBottom - fabTopFromViewportBottom
    const fabBottom = viewportBottom - fabBottomFromViewportBottom

    const overlapsFab = ctaRect.bottom > fabTop && ctaRect.top < fabBottom
    return !overlapsFab
  } catch {
    return false
  }
}

/**
 * Hidden until the inline CTA layout is idle, then true only if clear of the FAB chip.
 *
 * @param enabled - Re-run when the scroll tree mounts (e.g. after a loading gate).
 * @param resetKey - Re-attach when this changes (e.g. Elite ↔ Prime tab).
 */
export function useIsClearOfBottomFabZone(
  inlineCtaRef?: RefObject<HTMLElement> | null,
  scrollRootRef?: RefObject<HTMLElement> | null,
  enabled = true,
  resetKey?: string | number
): boolean {
  const [isClear, setIsClear] = useState(false)

  useEffect(() => {
    setIsClear(false)
    if (!enabled) return

    let cancelled = false
    let attachRafId = 0
    let paintRafId = 0
    let idleTimeoutId = 0
    let framesTried = 0
    let lastLayoutKey: string | null = null
    let layoutReady = false
    let scrollRoot: HTMLElement | null = null
    let resizeObserver: ResizeObserver | null = null
    let intersectionObserver: IntersectionObserver | null = null

    const clearIdleTimeout = () => {
      if (idleTimeoutId) {
        window.clearTimeout(idleTimeoutId)
        idleTimeoutId = 0
      }
    }

    const hideFab = () => {
      setIsClear((prev) => (prev ? false : prev))
    }

    const applyOverlapVisibility = (inlineCta: HTMLElement) => {
      const nextClear = isInlineCtaClearOfViewportFab(inlineCta)
      setIsClear((prev) => (prev === nextClear ? prev : nextClear))
    }

    const armLayoutIdle = () => {
      clearIdleTimeout()
      idleTimeoutId = window.setTimeout(() => {
        if (cancelled) return
        const inlineCta = inlineCtaRef?.current ?? null
        const root = scrollRootRef?.current ?? null
        if (!inlineCta?.isConnected || !root?.isConnected) {
          hideFab()
          return
        }
        const keyNow = readCtaLayoutKey(inlineCta, root)
        if (keyNow === null) return
        // The final layout shift may not emit another observer event, so keep waiting from this position.
        if (keyNow !== lastLayoutKey) {
          lastLayoutKey = keyNow
          armLayoutIdle()
          return
        }
        layoutReady = true
        applyOverlapVisibility(inlineCta)
      }, CTA_LAYOUT_IDLE_MS)
    }

    const updateFromDom = () => {
      if (cancelled) return
      const inlineCta = inlineCtaRef?.current ?? null
      const root = scrollRootRef?.current ?? null
      if (!inlineCta?.isConnected || !root?.isConnected) {
        layoutReady = false
        lastLayoutKey = null
        clearIdleTimeout()
        hideFab()
        return
      }

      const layoutKey = readCtaLayoutKey(inlineCta, root)
      if (layoutKey === null) {
        layoutReady = false
        clearIdleTimeout()
        hideFab()
        return
      }

      // Hero/content still shifting (Rive, images, etc.) — keep FAB hidden until idle.
      if (layoutKey !== lastLayoutKey) {
        lastLayoutKey = layoutKey
        layoutReady = false
        hideFab()
        armLayoutIdle()
        return
      }

      if (!layoutReady) {
        // Same key, still waiting for idle timeout.
        if (!idleTimeoutId) armLayoutIdle()
        return
      }

      // Layout trusted — normal overlap updates (scroll, etc.).
      applyOverlapVisibility(inlineCta)
    }

    const detachListeners = () => {
      clearIdleTimeout()
      try {
        scrollRoot?.removeEventListener('scroll', updateFromDom)
      } catch {
        // ignore
      }
      try {
        window.removeEventListener('resize', updateFromDom)
        window.visualViewport?.removeEventListener('resize', updateFromDom)
        window.visualViewport?.removeEventListener('scroll', updateFromDom)
      } catch {
        // ignore
      }
      try {
        resizeObserver?.disconnect()
      } catch {
        // ignore
      }
      try {
        intersectionObserver?.disconnect()
      } catch {
        // ignore
      }
      resizeObserver = null
      intersectionObserver = null
      scrollRoot = null
    }

    const attach = () => {
      if (cancelled) return
      const inlineCta = inlineCtaRef?.current ?? null
      const nextScrollRoot = scrollRootRef?.current ?? null

      if (!inlineCta?.isConnected || !nextScrollRoot?.isConnected) {
        framesTried += 1
        if (framesTried >= MAX_ATTACH_FRAMES) {
          hideFab()
          return
        }
        attachRafId = requestAnimationFrame(attach)
        return
      }

      scrollRoot = nextScrollRoot

      try {
        scrollRoot.addEventListener('scroll', updateFromDom, { passive: true })
        window.addEventListener('resize', updateFromDom)
        window.visualViewport?.addEventListener('resize', updateFromDom)
        window.visualViewport?.addEventListener('scroll', updateFromDom)
      } catch {
        // still measure below
      }

      if (typeof ResizeObserver !== 'undefined') {
        try {
          resizeObserver = new ResizeObserver(() => updateFromDom())
          resizeObserver.observe(inlineCta)
          resizeObserver.observe(scrollRoot)
          const scrollContent = scrollRoot.firstElementChild
          if (scrollContent instanceof HTMLElement) {
            resizeObserver.observe(scrollContent)
          }
        } catch {
          resizeObserver = null
        }
      }

      if (typeof IntersectionObserver !== 'undefined') {
        try {
          intersectionObserver = new IntersectionObserver(() => updateFromDom(), {
            root: null,
            threshold: [0, 0.01, 0.1, 0.25, 0.5, 0.75, 1]
          })
          intersectionObserver.observe(inlineCta)
        } catch {
          intersectionObserver = null
        }
      }

      paintRafId = requestAnimationFrame(() => {
        if (cancelled) return
        paintRafId = requestAnimationFrame(() => {
          if (cancelled) return
          updateFromDom()
        })
      })
    }

    attach()

    return () => {
      cancelled = true
      cancelAnimationFrame(attachRafId)
      cancelAnimationFrame(paintRafId)
      detachListeners()
    }
  }, [inlineCtaRef, scrollRootRef, enabled, resetKey])

  return isClear
}
